import axios, { AxiosResponse } from 'axios';

import config from '../../../../config/config';
import { ISubscriptionMembershipPlan } from '../../../../models/interfaces/CreateSubscriptionMembershipPlan';
import { ICreateKEOrganizationSubscriptionExtensionModel } from '../../../../models/interfaces/Subscription/KidnapAndExtortion/CreateKEOrganizationSubscriptionExtensionModel';
import { IEditKEOrganizationSubscriptionModel } from '../../../../models/interfaces/Subscription/KidnapAndExtortion/EditKEOrganizationSubscriptionModel';
import { IKEOrganizationSubscriptionOptionsModel } from '../../../../models/interfaces/Subscription/KidnapAndExtortion/KEOrganizationSubscriptionOptionsModel';
import { IKEOrganizationSubscriptionOverview } from '../../../../models/interfaces/Subscription/KidnapAndExtortion/KEOrganizationSubscriptionOverview';
import { ISubscriptionSummaryModel } from '../../../../models/interfaces/Subscription/SubscriptionSummaryModel';
import { IChangeSubscriptionPlanCapacityModel } from '../../../../models/interfaces/Subscription/UpdateSubscriptionPlanCapacityModel';
import { IOrganizationSubscriptionExtensionSummaryModel } from '../../../../models/interfaces/Subscription/OrganizationSubscriptionExtensionSummaryModel';
import { ISubscriptionDates } from '../../../../models/SubscriptionDates';
import {
  prepareQueryParams,
  QueryParamItem,
} from '../../../../utils/validations/queryParamsHelpers';
import { IOrganizationSubscriptionPlansModel } from '../common/models';
import { RenewKESubscriptionModel } from '../../../../models/types/KidnapAndExtortion/RenewKESubscriptionModel';
import { IOrganizationSubscription } from '../../../../models/interfaces/Subscription/OrganizationSubscription';
import { IRenewOrganizationSubscriptionAnalyseModel } from '../../../../models/IRenewOrganizationSubscriptionAnalyseModel';
import { IRenewOrganizationSubscriptionMembershipsModel } from '../../../../models/IRenewOrganizationSubscriptionMembershipsModel';
import { IKESubscriptionRenewalSuggestionModel } from '../../../../models/interfaces/Subscription/KidnapAndExtortion/KESubscriptionRenewalSuggestionModel';
import { RenewalStatus } from '../../../../models/enums/RenewalStatus';

export const getKESubscription = (
  organizationId: string,
  subscriptionId: string,
): Promise<AxiosResponse<IKEOrganizationSubscriptionOverview>> =>
  axios.get(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}`,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const getDatesForNewKESubscription = (
  organizationId: string,
  contractId: string,
): Promise<AxiosResponse<ISubscriptionDates>> => {
  const queryParams: QueryParamItem[] = [
    {
      name: 'contractId',
      value: contractId,
    },
  ];

  const params = prepareQueryParams({ queryParams });

  return axios.get(
    `api/organizations/${organizationId}/subscriptions/newDates${params}`,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );
};

export const getKESubscriptionSuggestion = (
  organizationId: string,
  subscriptionId: string,
  cancellationToken?: AbortSignal,
): Promise<AxiosResponse<IEditKEOrganizationSubscriptionModel>> =>
  axios.get(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/suggestion`,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
      signal: cancellationToken,
    },
  );

export const getKESubscriptionSummary = (
  organizationId: string,
  data: ISubscriptionMembershipPlan[],
): Promise<AxiosResponse<ISubscriptionSummaryModel>> =>
  axios.post(
    `api/organizations/${organizationId}/subscriptions/summary`,
    { membershipPlans: data },
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const updateKESubscriptionRequest = (
  organizationId: string,
  subscriptionId: string,
  data: IEditKEOrganizationSubscriptionModel,
): Promise<AxiosResponse> =>
  axios.put(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}`,
    data,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const deleteKESubscriptionRequest = (
  organizationId: string,
  subscriptionId: string,
  cancellationToken?: AbortSignal,
): Promise<AxiosResponse<void>> =>
  axios.delete(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}`,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
      signal: cancellationToken,
    },
  );

export const updateKESubscriptionDetails = (
  organizationId: string,
  subscriptionId: string,
  subscriptionDetails: IKEOrganizationSubscriptionOptionsModel,
): Promise<AxiosResponse<IKEOrganizationSubscriptionOverview>> =>
  axios.put(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/details`,
    subscriptionDetails,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const extendKESubscriptionRequest = (
  organizationId: string,
  subscriptionId: string,
  subscriptionExtension: ICreateKEOrganizationSubscriptionExtensionModel,
): Promise<AxiosResponse<IKEOrganizationSubscriptionOverview>> =>
  axios.post(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/capacity/update`,
    subscriptionExtension,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const getKESubscriptionExtensionSummary = (
  organizationId: string,
  subscriptionId: string,
  data: IChangeSubscriptionPlanCapacityModel[],
): Promise<AxiosResponse<IOrganizationSubscriptionExtensionSummaryModel>> =>
  axios.post(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/capacity/update/summary`,
    data,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const getKESubscriptionPlans = (
  organizationId: string,
  subscriptionId: string,
  primaryMemberBirthDate?: string,
  membershipEffectiveDate?: string,
  cancellationToken?: AbortSignal,
): Promise<AxiosResponse<IOrganizationSubscriptionPlansModel>> => {
  const queryParams: QueryParamItem[] = [
    {
      name: 'primaryMemberBirthDate',
      value: primaryMemberBirthDate,
    },
    {
      name: 'membershipEffectiveDate',
      value: membershipEffectiveDate,
    },
  ];

  const params = prepareQueryParams({ queryParams });

  return axios.get(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/plans${params}`,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
      signal: cancellationToken,
    },
  );
};

export const getKESubscriptionRenewalSuggestion = (
  organizationId: string,
  subscriptionId: string,
): Promise<AxiosResponse<IKESubscriptionRenewalSuggestionModel>> =>
  axios.get(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/renewalsuggestion`,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const renewKESubscriptionRequest = (
  organizationId: string,
  subscriptionId: string,
  data: RenewKESubscriptionModel,
): Promise<AxiosResponse<IOrganizationSubscription>> =>
  axios.post(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/renew`,
    data,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const getKESubscriptionRenewalAnalyseRequest = (
  organizationId: string,
  subscriptionId: string,
): Promise<AxiosResponse<IRenewOrganizationSubscriptionAnalyseModel>> =>
  axios.get(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/memberships/renewalanalysis`,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const sendKESubscriptionRenewalMembershipsRequest = (
  organizationId: string,
  subscriptionId: string,
  data: IRenewOrganizationSubscriptionMembershipsModel,
): Promise<AxiosResponse<IRenewOrganizationSubscriptionMembershipsModel>> =>
  axios.post(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/memberships/toberenewed`,
    data,
    {
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );

export const editKESubscriptionRenewalStatusRequest = (
  organizationId: string,
  subscriptionId: string,
  renewalStatus: RenewalStatus,
  cancellationToken: AbortSignal,
): Promise<AxiosResponse<void>> =>
  axios.post(
    `/api/organizations/${organizationId}/subscriptions/${subscriptionId}/renewalstatus`,
    { renewalStatus },
    {
      signal: cancellationToken,
      baseURL: config.kidnapAndExtortionApi.baseUrl,
    },
  );
